import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, ShieldCheck, Store, Trash2, Pencil, X } from "lucide-react";
import { api, formatError, fileUrl } from "@/lib/api";
import { Page } from "@/components/Page";
import { MediaUploader } from "@/components/Uploader";

const emptyForm = {
  name: "", gi_name: "", category_id: "", description: "",
  color: "", size: "", unit: "piece", stock: 1,
  mrp: "", gst_pct: 0, discount_pct: 0,
  specifications: [], images: [], videos: [], on_front_page: false,
};

function computeFinal(mrp, gst, disc) {
  const m = Number(mrp) || 0;
  const d = m * (1 - (Number(disc) || 0) / 100);
  return Math.round((d + d * (Number(gst) || 0) / 100) * 100) / 100;
}

export default function Marketplace() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [show, setShow] = useState(false);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState(emptyForm);

  const load = async () => {
    const [pd, ct] = await Promise.all([api.get("/dashboard"), api.get("/categories")]);
    setProducts(pd.data.products || []);
    setCategories(ct.data || []);
  };
  useEffect(() => { load().catch(() => {}); }, []);

  const final = useMemo(() => computeFinal(form.mrp, form.gst_pct, form.discount_pct), [form.mrp, form.gst_pct, form.discount_pct]);

  const change = (k, v) => setForm({ ...form, [k]: v });
  const setSpec = (i, patch) => setForm({ ...form, specifications: form.specifications.map((s, idx) => idx === i ? { ...s, ...patch } : s) });
  const addSpec = () => setForm({ ...form, specifications: [...form.specifications, { label: "", value: "" }] });
  const rmSpec = (i) => setForm({ ...form, specifications: form.specifications.filter((_, idx) => idx !== i) });

  const startEdit = (p) => {
    setEditing(p);
    setForm({
      name: p.name || "", gi_name: p.gi_name || "", category_id: p.category_id || "",
      description: p.description || "", color: p.color || "", size: p.size || "",
      unit: p.unit || "piece", stock: p.stock || 0, mrp: p.mrp || "",
      gst_pct: p.gst_pct || 0, discount_pct: p.discount_pct || 0,
      specifications: p.specifications || [],
      images: (p.images || []).map(x => (typeof x === "string" ? { path: x, kind: "image" } : x)),
      videos: (p.videos || []).map(x => (typeof x === "string" ? { path: x, kind: "video" } : x)),
      on_front_page: !!p.on_front_page,
    });
    setShow(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const cancelEdit = () => { setEditing(null); setForm(emptyForm); setShow(false); };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.mrp || Number(form.mrp) <= 0) { toast.error("Please enter a MRP greater than 0"); return; }
    if (!form.category_id) { toast.error("Please choose a category"); return; }
    setBusy(true);
    try {
      const payload = {
        ...form,
        mrp: Number(form.mrp),
        gst_pct: Number(form.gst_pct || 0),
        discount_pct: Number(form.discount_pct || 0),
        stock: Number(form.stock || 0),
        images: (form.images || []).map(m => m.path || m).filter(Boolean),
        videos: (form.videos || []).map(m => m.path || m).filter(Boolean),
        specifications: form.specifications.filter(s => s.label && s.value),
      };
      if (editing) {
        const r = await api.put(`/products/${editing.id}`, payload);
        setProducts(products.map(x => x.id === editing.id ? r.data : x));
        toast.success("Product updated");
      } else {
        const r = await api.post("/products", payload);
        setProducts([r.data, ...products]);
        toast.success("Product listed");
      }
      cancelEdit();
    } catch (err) {
      toast.error(formatError(err));
    } finally { setBusy(false); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Remove "${p.name}" from the marketplace?`)) return;
    try {
      await api.delete(`/products/${p.id}`);
      setProducts(products.filter(x => x.id !== p.id));
      toast.success("Product removed");
    } catch (err) { toast.error(formatError(err)); }
  };

  const toMediaList = (list = [], kind = "image") => list.map(p => (typeof p === "string" ? { path: p, kind } : p));

  return (
    <Page title="Marketplace" subtitle="List authentic GI products with photos, videos, specifications, and pricing.">
      <div className="toolbar">
        <div>
          <p className="eyebrow">MY LISTINGS</p>
          <h3>{products.length || 0} products live</h3>
        </div>
        <button className="primary-btn" onClick={() => (show ? cancelEdit() : setShow(true))} data-testid="add-product-button">
          {show ? <><X size={15} /> Close</> : <><Plus size={16} /> List a product</>}
        </button>
      </div>

      {show && (
        <form className="form-section" onSubmit={submit} style={{ marginBottom: 20 }}>
          {editing && <div className="notice" data-testid="edit-mode-banner"><Pencil size={16} /><div><b>Editing "{editing.name}"</b><p>Update any field below and click Save changes.</p></div></div>}
          <div className="field-grid">
            <label>Product name<input required value={form.name} onChange={e => change("name", e.target.value)} data-testid="product-name-input" placeholder="e.g., Handloom silk saree" /></label>
            <label>Related GI item name<input required value={form.gi_name} onChange={e => change("gi_name", e.target.value)} data-testid="product-gi-input" placeholder="e.g., Mysore Silk" /></label>
            <label>Category
              <select required value={form.category_id} onChange={e => change("category_id", e.target.value)} data-testid="product-category-select">
                <option value="">Select category</option>
                {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </label>
            <label>Unit<select value={form.unit} onChange={e => change("unit", e.target.value)} data-testid="product-unit-select">
              <option>piece</option><option>pair</option><option>kg</option><option>gram</option><option>metre</option><option>dozen</option><option>set</option>
            </select></label>
            <label>Colour<input value={form.color} onChange={e => change("color", e.target.value)} data-testid="product-color-input" placeholder="e.g., Kesari orange" /></label>
            <label>Size / dimensions<input value={form.size} onChange={e => change("size", e.target.value)} data-testid="product-size-input" placeholder="e.g., 6.3 m / L / 20×30 cm" /></label>
            <label>Stock<input type="number" min="0" value={form.stock} onChange={e => change("stock", e.target.value)} data-testid="product-stock-input" /></label>
            <label>MRP (₹)<input required type="number" min="0" step="0.01" value={form.mrp} onChange={e => change("mrp", e.target.value)} data-testid="product-mrp-input" /></label>
            <label>GST (%)<input type="number" min="0" max="28" step="0.1" value={form.gst_pct} onChange={e => change("gst_pct", e.target.value)} data-testid="product-gst-input" /></label>
            <label>Discount (%)<input type="number" min="0" max="90" step="0.1" value={form.discount_pct} onChange={e => change("discount_pct", e.target.value)} data-testid="product-discount-input" /></label>
            <label>Final selling price<input readOnly value={`₹${final.toLocaleString("en-IN")}`} data-testid="product-final-price" /></label>
          </div>
          <label>Description<textarea value={form.description} onChange={e => change("description", e.target.value)} data-testid="product-description-input" placeholder="Tell buyers about craft, technique, and origin" /></label>

          <div style={{ marginTop: 14 }}>
            <div className="form-top">
              <div><p className="eyebrow">SPECIFICATIONS</p><h4>Key details</h4></div>
              <button type="button" className="secondary-btn" onClick={addSpec} data-testid="add-spec-button"><Plus size={13} /> Add spec</button>
            </div>
            {form.specifications.length === 0 && <p className="muted" style={{ fontSize: 12 }}>Add label / value pairs like "Weight — 500 g", "Material — Pure silk"…</p>}
            <div className="rows">
              {form.specifications.map((s, i) => (
                <div className="row-inline" key={i} data-testid={`spec-row-${i}`}>
                  <input placeholder="Label (e.g., Material)" value={s.label} onChange={e => setSpec(i, { label: e.target.value })} data-testid={`spec-${i}-label`} />
                  <input placeholder="Value (e.g., Pure silk)" value={s.value} onChange={e => setSpec(i, { value: e.target.value })} data-testid={`spec-${i}-value`} />
                  <button type="button" className="danger-btn" onClick={() => rmSpec(i)} data-testid={`spec-${i}-remove`}><Trash2 size={12} /></button>
                </div>
              ))}
            </div>
          </div>

          <MediaUploader label="Product photos (up to 4)" value={toMediaList(form.images, "image")} onChange={list => change("images", list)} accept="image/*" max={4} testIdPrefix="product-images" />
          <MediaUploader label="Product videos (up to 3)" value={toMediaList(form.videos, "video")} onChange={list => change("videos", list)} accept="video/*" max={3} testIdPrefix="product-videos" />

          <label className="check-line" style={{ marginTop: 8 }}>
            <input type="checkbox" checked={form.on_front_page} onChange={e => change("on_front_page", e.target.checked)} data-testid="product-front-page-toggle" />
            <span>Request feature on the home page (subject to admin approval)</span>
          </label>

          <div className="form-actions">
            <span className="muted">Buyers view this listing on the storefront.</span>
            <div style={{ display: "flex", gap: 8 }}>
              {editing && <button type="button" className="secondary-btn" onClick={cancelEdit} data-testid="product-cancel-edit">Cancel</button>}
              <button className="primary-btn" disabled={busy} data-testid="product-save-button">
                {busy ? (editing ? "Saving…" : "Publishing…") : (editing ? "Save changes" : "Publish product")}
              </button>
            </div>
          </div>
        </form>
      )}

      <div className="product-grid">
        {products.length ? products.map(p => (
          <div className="product-card" key={p.id} data-testid={`product-${p.id}`}>
            <div className="product-art">
              {p.images?.[0] ? <img src={fileUrl(p.images[0])} alt={p.name} /> : <span className="placeholder">✦</span>}
            </div>
            <div className="product-body">
              {p.verified
                ? <span className="verified"><ShieldCheck size={12} /> GI seller verified</span>
                : <span className="verified pending"><ShieldCheck size={12} /> Awaiting verification</span>}
              <h4>{p.name}</h4>
              <p>{p.gi_name}</p>
              <div className="price-row">
                <span className="now">₹{Number(p.final_price).toLocaleString("en-IN")}</span>
                {p.discount_pct > 0 && <span className="mrp">₹{Number(p.mrp).toLocaleString("en-IN")}</span>}
              </div>
              <div style={{ display: "flex", gap: 6, marginTop: 8 }}>
                <button className="secondary-btn" onClick={() => startEdit(p)} data-testid={`edit-product-${p.id}`}><Pencil size={12} /> Edit</button>
                <button className="danger-btn" onClick={() => remove(p)} data-testid={`delete-product-${p.id}`}><Trash2 size={12} /> Delete</button>
              </div>
            </div>
          </div>
        )) : (
          <div className="empty-state">
            <Store size={26} />
            <h3>Your marketplace starts here</h3>
            <p>List a product to make your GI story discoverable to buyers.</p>
          </div>
        )}
      </div>
    </Page>
  );
}
